import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { IssueItem, ProjectIssuesService } from "../services/project-issues.service";
import { ProjectService } from "../services/project.service";

interface IssueProjectOption {
  id: number | string;
  project_name: string;
}

@Component({
  selector: "app-all-issues",
  templateUrl: "./all-issues.component.html",
  styleUrls: ["./all-issues.component.css"],
})
export class AllIssuesComponent implements OnInit, OnDestroy {
  issues: IssueItem[] = [];
  projects: IssueProjectOption[] = [];
  loading = false;
  projectsLoading = false;
  errorMessage = "";
  filters = {
    search: "",
    project: "",
    status: "",
  };
  readonly statusOptions = ["Pending", "Resolved", "Forwarded"];

  private readonly search$ = new Subject<string>();
  private searchSubscription?: Subscription;

  constructor(
    private projectIssuesService: ProjectIssuesService,
    private projectService: ProjectService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.searchSubscription = this.search$
      .pipe(debounceTime(350), distinctUntilChanged())
      .subscribe(() => this.loadIssues());

    this.loadProjects();
    this.loadIssues();
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  loadIssues(): void {
    this.loading = true;
    this.errorMessage = "";

    this.projectIssuesService.listIssues(this.getIssueQuery()).subscribe(
      (res) => {
        this.issues = res || [];
        this.loading = false;
      },
      () => {
        this.loading = false;
        this.errorMessage = "Unable to load issues.";
      }
    );
  }

  loadProjects(): void {
    this.projectsLoading = true;

    this.projectService.allProjectList().subscribe(
      (res) => {
        const items = Array.isArray(res) ? res : res?.results || [];
        this.projects = [
          { id: "", project_name: "All Projects" },
          ...(items || []).map((item: any) => ({
            id: item?.id || item?.project_id,
            project_name: item?.project_name || item?.name || `Project ${item?.id}`,
          })),
        ];
        this.projectsLoading = false;
      },
      () => {
        this.projectsLoading = false;
      }
    );
  }

  onSearchInput(value: string): void {
    this.filters.search = value || "";
    this.search$.next((value || "").trim());
  }

  onProjectChange(): void {
    this.loadIssues();
  }

  onStatusChange(): void {
    this.loadIssues();
  }

  clearFilters(): void {
    this.filters = {
      search: "",
      project: "",
      status: "",
    };
    this.loadIssues();
  }

  openIssue(issue: IssueItem): void {
    if (!issue || !issue.id) return;
    this.router.navigate(["/all-issues", issue.id]);
  }

  getStatusClass(status: string): string {
    if (status === "Resolved") return "status-resolved";
    if (status === "Forwarded") return "status-forwarded";
    return "status-pending";
  }

  trackByIssueId(index: number, issue: IssueItem): number | string {
    return issue?.id || index;
  }

  private getIssueQuery(): {
    project?: string;
    status?: string;
    search?: string;
  } {
    const project = (this.filters.project || "").toString().trim();
    const status = (this.filters.status || "").trim();
    const search = (this.filters.search || "").trim();

    return {
      project: project || undefined,
      status: status || undefined,
      search: search || undefined,
    };
  }
}
